/**
 * Scenario Templates for Fjernvarme Simulation
 * Defines predefined configurations and constraints
 */

/**
 * Monthly heat demand distribution templates
 * Values in GWh per month for standard year
 */
export const MONTHLY_DISTRIBUTIONS = {
  // Skælskør case - based on real 2024 data
  SKAELSKOER: {
    name: "Skælskør 2024",
    totalGWh: 45.589,
    monthly: null, // Use actual hourly data from simulationData2024.json
  },

  // Small case - 15 GWh annual
  SMALL_15GWH: {
    name: "Lille anlæg 15 GWh",
    totalGWh: 15.0,
    monthly: [3, 1.5, 1.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 1.5, 3, 3], // GWh per month
  },
  SMALL_15GWH_NET: {
    name: "Lille anlæg 15 GWh (NET)",
    totalGWh: 20.0,
    monthly: [3, 1.5, 1.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 1.5, 3, 3], // GWh per month
  },

  // Custom - user defined
  CUSTOM: {
    name: "Brugerdefineret",
    totalGWh: 0,
    monthly: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  },
};

/**
 * Available energy source configurations
 */
export const ENERGY_SOURCES = {
  GRID_ONLY: {
    name: "Kun Elnet",
    grid: true,
    solar: false,
    wasteHeat: false,
  },
  GRID_SOLAR: {
    name: "Elnet + Sol",
    grid: true,
    solar: true,
    wasteHeat: false,
  },
  GRID_WASTE: {
    name: "Elnet + Overskudsvarme",
    grid: true,
    solar: false,
    wasteHeat: true,
  },
  ALL_SOURCES: {
    name: "Alle kilder (Skælskør)",
    grid: true,
    solar: true,
    wasteHeat: true,
  },
};

/**
 * Equipment dimension constraints
 */
export const EQUIPMENT_CONSTRAINTS = {
  heater: {
    name: "Elkedel",
    unit: "MW",
    options: [10, 15, 20, 25, 30, 35, 40, 45],
    default: 30,
  },
  htes: {
    name: "HTES Moduler",
    unit: "x 150 MWh",
    options: [1, 2],
    capacityPerModule: 150, // MWh per module
    default: 2,
  },
  turbine: {
    name: "Turbine",
    unit: "MW",
    options: [10, 20, 30],
    default: 10,
    // Dynamic efficiency based on storage temperature
    minEfficiency: { heat: 0.75, electricity: 0.10 },
    maxEfficiency: { heat: 0.90, electricity: 0.20 },
  },
  ltes: {
    name: "LTES",
    unit: "MWh",
    capacity: 205, // Fixed capacity for now
    default: 205,
  },
  battery: {
    name: "Batteri",
    unit: "MW",
    options: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    default: 3,
  },
};

/**
 * Predefined scenario templates
 */
export const SCENARIO_TEMPLATES = {
  SKAELSKOER_FULL: {
    id: "skaelskoer-full",
    name: "Skælskør Full (45 GWh)",
    description: "Fuld Skælskør konfiguration med alle energikilder",
    config: {
      // Demand
      demandProfile: "SKAELSKOER",
      annualDemandGWh: 45.589,
      monthlyDistribution: null, // Use real data

      // Energy sources
      energySources: ENERGY_SOURCES.ALL_SOURCES,

      // Equipment dimensions
      heater_MW: 30,
      htes_modules: 2,
      htes_capacity_MWh: 300, // 2 x 150 MWh
      ltes_capacity_MWh: 205,
      turbine_MW: 10,
      battery_MW: 3,
      battery_MWh: 3, // 1 hour storage

      // Efficiency parameters
      heater_to_ltes: 0.97, // 3% production loss
      heater_to_htes: 0.97,
      ltes_discharge: 1.0, // No discharge loss for LTES
      turbine_efficiency_heat: 0.85, // Dynamic based on temperature
      turbine_efficiency_electricity: 0.15,
      turbine_dynamic: true, // Enable dynamic efficiency
      grid_loss: 0.10, // 10% network loss
      battery_roundtrip: 0.92, // 92% round-trip efficiency

      // Thermal physics
      turbine_min_inlet_temp_c: 90, // Minimum HTES temp for turbine operation
      enable_thermal_constraints: true,
    },
  },

  SMALL_GRID_ONLY_15: {
    id: "small-grid-only-15",
    name: "Lille anlæg - kun elnet (15 GWh)",
    description: "15 GWh årligt forbrug, kun elnet som energikilde",
    config: {
      // Demand
      demandProfile: "SMALL_15GWH",
      annualDemandGWh: 15.0,
      monthlyDistribution: [3, 1.5, 1.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 1.5, 3, 3],

      // Energy sources
      energySources: ENERGY_SOURCES.GRID_ONLY,

      // Equipment dimensions
      heater_MW: 15,
      htes_modules: 1,
      htes_capacity_MWh: 150, // 1 x 150 MWh
      ltes_capacity_MWh: 205,
      turbine_MW: 10,
      battery_MW: 5,
      battery_MWh: 5, // 1 hour storage

      // Efficiency parameters
      heater_to_ltes: 0.97,
      heater_to_htes: 0.97,
      ltes_discharge: 1.0,
      turbine_efficiency_heat: 0.85,
      turbine_efficiency_electricity: 0.15,
      turbine_dynamic: true,
      grid_loss: 0.10,
      battery_roundtrip: 0.92,

      // Thermal physics
      turbine_min_inlet_temp_c: 90,
      enable_thermal_constraints: true,
    },
  },

  CUSTOM: {
    id: "custom",
    name: "Brugerdefineret",
    description: "Opret din egen konfiguration",
    config: {
      demandProfile: "CUSTOM",
      annualDemandGWh: 0,
      monthlyDistribution: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      energySources: ENERGY_SOURCES.GRID_ONLY,
      heater_MW: 30,
      htes_modules: 2,
      htes_capacity_MWh: 300,
      ltes_capacity_MWh: 205,
      turbine_MW: 10,
      battery_MW: 3,
      battery_MWh: 3,
      heater_to_ltes: 0.97,
      heater_to_htes: 0.97,
      ltes_discharge: 1.0,
      turbine_efficiency_heat: 0.85,
      turbine_efficiency_electricity: 0.15,
      turbine_dynamic: true,
      grid_loss: 0.10,
      battery_roundtrip: 0.92,
      turbine_min_inlet_temp_c: 90,
      enable_thermal_constraints: true,
    },
  },
};

/**
 * Validate scenario configuration
 * @param {Object} config - Scenario configuration
 * @returns {Object} Validation result with errors/warnings
 */
export const validateScenarioConfig = (config) => {
  const errors = [];
  const warnings = [];

  // Validate demand
  if (!config.annualDemandGWh || config.annualDemandGWh <= 0) {
    errors.push("Årligt varmebehov skal være større end 0 GWh");
  }

  // Validate monthly distribution if provided
  if (config.monthlyDistribution) {
    const total = config.monthlyDistribution.reduce((sum, val) => sum + val, 0);
    if (Math.abs(total - config.annualDemandGWh) > 0.1) {
      warnings.push(`Månedlig fordeling summerer til ${total.toFixed(2)} GWh, men årligt behov er ${config.annualDemandGWh} GWh`);
    }
  }

  // Validate heater capacity
  if (!EQUIPMENT_CONSTRAINTS.heater.options.includes(config.heater_MW)) {
    errors.push(`Elkedel kapacitet skal være en af: ${EQUIPMENT_CONSTRAINTS.heater.options.join(', ')} MW`);
  }

  // Validate turbine capacity
  if (!EQUIPMENT_CONSTRAINTS.turbine.options.includes(config.turbine_MW)) {
    errors.push(`Turbine kapacitet skal være en af: ${EQUIPMENT_CONSTRAINTS.turbine.options.join(', ')} MW`);
  }

  // Validate battery capacity
  if (!EQUIPMENT_CONSTRAINTS.battery.options.includes(config.battery_MW)) {
    errors.push(`Batteri kapacitet skal være en af: ${EQUIPMENT_CONSTRAINTS.battery.options.join(', ')} MW`);
  }

  // Validate HTES modules
  if (!EQUIPMENT_CONSTRAINTS.htes.options.includes(config.htes_modules)) {
    errors.push(`HTES moduler skal være 1 eller 2`);
  }

  // Check heater vs demand ratio
  const peakDemandMW = (config.annualDemandGWh * 1000) / 8783 * 3; // Rough estimate: 3x average for winter peak
  if (config.heater_MW < peakDemandMW * 0.5) {
    warnings.push(`Elkedel kapacitet (${config.heater_MW} MW) kan være for lille til peak demand (~${peakDemandMW.toFixed(0)} MW)`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Calculate derived parameters from config
 * @param {Object} config - Scenario configuration
 * @returns {Object} Derived parameters
 */
export const calculateDerivedParameters = (config) => {
  // Calculate NET delivery target (after all losses)
  const netDeliveryTarget = config.annualDemandGWh; // GWh

  // Calculate AB-plant target (before grid loss)
  const abPlantTarget = netDeliveryTarget / (1 - config.grid_loss);

  // Calculate AN-plant target (before production loss)
  const anPlantTarget = abPlantTarget / (1 - (1 - config.heater_to_ltes));

  // Calculate electricity input needed (assuming COP ~1.0 for electric heater)
  const electricityInputNeeded = anPlantTarget; // GWh

  // Calculate average hourly demand
  const avgHourlyDemandMW = (netDeliveryTarget * 1000) / 8783; // Convert GWh to MWh, divide by hours

  // Calculate peak demand estimate (winter months are ~3x average)
  const peakDemandMW = avgHourlyDemandMW * 3;

  return {
    netDeliveryTarget, // GWh
    abPlantTarget, // GWh
    anPlantTarget, // GWh
    electricityInputNeeded, // GWh
    avgHourlyDemandMW, // MW
    peakDemandMW, // MW (estimated)
    heaterUtilizationHours: (electricityInputNeeded * 1000) / config.heater_MW, // Hours
    storageCapacityDays: (config.htes_capacity_MWh + config.ltes_capacity_MWh) / (avgHourlyDemandMW * 24), // Days
  };
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {  
  MONTHLY_DISTRIBUTIONS,
  ENERGY_SOURCES,
  EQUIPMENT_CONSTRAINTS,
  SCENARIO_TEMPLATES,
  validateScenarioConfig,
  calculateDerivedParameters,
};
